import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { ResizeController } from '../../controllers/resize-controller';
import innerStyles from './styles/outline-main-header.base.pcss?inline';
import globalStyles from './styles/outline-main-header.global.pcss?inline';
import {
  modifyMarkup,
  handleESC,
  resizeReset,
  clickAwayCloseHandler,
  mobileMenuDisplayHandler,
  removeAllOpen,
  setupSearchPanelFocusHandler,
  removeSearchPanelFocusHandler,
  setupSearchPanelFocusTrap,
  removeSearchPanelFocusTrap,
  setupMobileMenuFocusTrap,
  removeMobileMenuFocusTrap
} from './main-header-utilities';
import '../outline-accent-bar/outline-accent-bar';
import '../outline-container/outline-container';
import '../outline-svg/outline-svg';
import '../outline-logo/outline-logo';
import '../outline-icon/outline-icon';

/**
 * The OutlineMainHeader component
 * @element outline-main-header
 */
@customElement('outline-main-header')
export class OutlineMainHeader extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [1024],
  });

  @property({ type: Boolean, attribute: 'scroll-lock', reflect: true })
  scrollLock: boolean;

  @property({ type: Boolean, reflect: true })
  isMobile: boolean;

  @property({ type: Boolean, reflect: true, attribute: 'search-open' })
  searchOpen: boolean;

  @property({ type: Boolean, reflect: true })
  mobileOpen: boolean;

  private _seed = Math.floor(Math.random() * 100000000).toString();

  private _keydownListener = (e: KeyboardEvent) => handleESC(e);
  private _clickListener = (e: Event) => clickAwayCloseHandler(e);

  firstUpdated() {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    modifyMarkup(this, this._seed);
    window.addEventListener('keydown', this._keydownListener);
    window.addEventListener('click', this._clickListener);

    // Setup search panel focus handling
    setupSearchPanelFocusHandler(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._keydownListener);
    window.removeEventListener('click', this._clickListener);
    removeSearchPanelFocusHandler(this);
    removeSearchPanelFocusTrap(this);
    removeMobileMenuFocusTrap(this);
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    const status = this.resizeController.currentBreakpointRange === 0;
    if (status !== this.isMobile) {
      this.isMobile = this.resizeController.currentBreakpointRange === 0;
      resizeReset(this);
      if (!this.isMobile) {
        modifyMarkup(this, this._seed);
      }
    }

    // If searchOpen property changed, update focus trap
    if (changedProperties.has('searchOpen')) {
      if (this.searchOpen) {
        // Set up focus trap when search panel opens
        setupSearchPanelFocusTrap(this);
      } else {
        // Remove focus trap when search panel closes
        removeSearchPanelFocusTrap(this);
      }
    }

    // If mobileOpen property changed, update focus trap
    if (changedProperties.has('mobileOpen')) {
      if (this.mobileOpen) {
        // Set up focus trap when mobile menu opens
        setupMobileMenuFocusTrap(this);
      } else {
        // Remove focus trap when mobile menu closes
        removeMobileMenuFocusTrap(this);
      }
    }
  }

  render(): TemplateResult {
    return html`
    <header>
      <outline-accent-bar></outline-accent-bar>
      <outline-container background-color="brand-neutral" y-padding="space-0">
        <div class="outline-main-header">
          ${this.isMobile ? this.mobileTemplate() : this.desktopTemplate()}
        </div>
      </outline-container>
    </header>
    `;
  }

  desktopTemplate() {
    return html`
      <div class="outline-main-header--top-rail">
        <div class="outline-main-header--logo">
          <slot name=logo></slot>

        </div>
        <div class="outline-main-header--utility">
          <slot name="utility"></slot>
        </div>
      </div>
      <div class="outline-main-header--main-content">
        <div class="outline-main-header--nav">
          <slot name="nav"></slot>
        </div>
        <div class="outline-main-header--search">
          <button
            @click=${this.toggleSearchOpen}
            aria-label="Search"
            aria-controls="outline-main-header--search-panel"
            aria-expanded=${this.searchOpen ? 'true' : 'false'}
            type="button"
          >
            <outline-icon icon-classes="fa-solid fa-magnifying-glass" color="var(--text-brand-secondary)"></outline-icon>
            <span>Search</span>
          </button>

        </div>
        <div class="outline-main-header--cta">
          <slot name="cta"></slot>
        </div>
      </div>
      <div id="outline-main-header--search-panel" class="outline-main-header--search-panel">
        <slot name="search-panel"></slot>
      </div>
    `;
  }

  mobileTemplate() {
    return html`
      <div class="outline-main-header--mobile--top-rail">
        <div class="outline-main-header--mobile--logo">
          <slot name="logo"></slot>
        </div>
        <div class="outline-main-header--mobile--function">
          <div class="outline-main-header--search-toggle">
            <button class="outline-main-header--mobile-toggle"
              aria-label="Search"
              aria-controls="outline-main-header--search-panel"
              aria-expanded=${this.searchOpen ? 'true' : 'false'}
              type="button"
              @click=${this.toggleSearchOpen}
              >
              <outline-svg
                size="25px"
                name="search"
                svg-fill="var(--background-brand-primary-default)"

              ></outline-svg>
            </button>
          </div>
          <div class="outline-main-header--menu-toggle">
            <button
              aria-label=${this.mobileOpen ? 'Close Menu' : 'Open Menu'}
              class="outline-main-header--mobile-toggle"
              aria-controls="outline-main-header--mobile-menu"
              aria-expanded=${this.mobileOpen ? 'true' : 'false'}
              type="button"
              @click=${() => mobileMenuDisplayHandler(this)}
            >
              <outline-svg
                size="25px"
                name="${this.mobileOpen ? 'cross' : 'hamburger'}"
                svg-fill="var(--background-brand-primary-default)"
                svg-color="var(--background-brand-primary-default)"
              ></outline-svg>
            </button>
          </div>
        </div>
      </div>
      <div id="outline-main-header--mobile-menu" class="outline-main-header--mobile--main-content">
        <div class="outline-main-header--mobile--nav">
          <slot name="nav"></slot>
        </div>
        <div class="outline-main-header--mobile--utility">
          <slot name="utility"></slot>
          <slot name="cta"></slot>
        </div>
      </div>
      <div id="outline-main-header--search-panel" class="outline-main-header--search-panel">
        <slot name="search-panel"></slot>
      </div>
    `;
  }

  toggleSearchOpen(event?: Event) {
    event?.stopPropagation();

    const willOpen = !this.searchOpen;

    if (willOpen) {
      removeAllOpen(false);
    }

    this.searchOpen = willOpen;

    // The focus trap will be automatically set up in the updated() method
    // when the searchOpen property changes, and focus will return to the
    // search button when closing via the removeSearchPanelFocusTrap function
  }
}
